import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import {
  Zap, Loader2, CheckCircle2, XCircle, ArrowLeft, Search, ChevronLeft, ChevronRight,
  RefreshCw, Filter, X,
} from 'lucide-react';
import AutomationRunDetailsModal from '@/components/AutomationRunDetailsModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AUTOMATION_OPTIONS = [
  { id: '', label: 'All automations' },
  { id: 'compliance_weekly', label: 'ISO 30401 Compliance Auto-Evaluation' },
  { id: 'certification_daily', label: 'Certification Expiry Scan' },
  { id: 'assessment_reminder', label: 'Capability Assessment Reminders' },
  { id: 'renewal_quarterly_digest', label: 'Quarterly Renewal Readiness Digest' },
  { id: 'readiness_watchdog', label: 'Email Pipeline Readiness Watchdog' },
  { id: 'automation_health_digest', label: 'Weekly Automation Health Digest' },
];

export default function AutomationRunsLogPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [automationId, setAutomationId] = useState(searchParams.get('automation_id') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [triggeredBy, setTriggeredBy] = useState(searchParams.get('triggered_by') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [since, setSince] = useState(searchParams.get('since') || '');
  const [until, setUntil] = useState(searchParams.get('until') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const pageSize = 25;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        automation_id: automationId,
        status,
        triggered_by: triggeredBy,
        search,
        since,
        until,
        page,
        page_size: pageSize,
      };
      const res = await axios.get(`${API}/admin/automations/runs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Admin or HR role required');
        navigate('/dashboard');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load automation runs');
      }
    } finally { setLoading(false); }
  }, [token, automationId, status, triggeredBy, search, since, until, page, navigate]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Sync URL state so the page is shareable / deep-linkable from the sparkline
  useEffect(() => {
    const next = {};
    if (automationId) next.automation_id = automationId;
    if (status) next.status = status;
    if (triggeredBy) next.triggered_by = triggeredBy;
    if (search) next.search = search;
    if (since) next.since = since;
    if (until) next.until = until;
    if (page > 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [automationId, status, triggeredBy, search, since, until, page, setSearchParams]);

  const clearFilters = () => {
    setAutomationId(''); setStatus(''); setTriggeredBy(''); setSearch('');
    setSince(''); setUntil(''); setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const hasActiveFilters = automationId || status || triggeredBy || search || since || until;

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4" data-testid="automation-runs-log-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/automations')} className="mb-1 -ml-2 text-[11px] text-[#6B7280]" data-testid="back-to-automations-btn">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to automations
          </Button>
          <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#002FA7]" /> Automation Run Log
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Full history of every scheduled and manually-triggered automation run. Click a row to see the full outcome payload.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRuns} data-testid="refresh-runs-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-3 gap-3" data-testid="runs-summary">
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Matching runs</p>
              <p className="text-2xl font-bold text-[#0A0A0A] mt-1" data-testid="summary-total">{data.total}</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Successes</p>
              <p className="text-2xl font-bold text-[#34C759] mt-1 flex items-center gap-2" data-testid="summary-success">
                {data.success_count}
                <CheckCircle2 className="w-4 h-4" />
              </p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Failures</p>
              <p className={`text-2xl font-bold mt-1 flex items-center gap-2 ${data.failure_count > 0 ? 'text-[#FF3B30]' : 'text-[#9CA3AF]'}`} data-testid="summary-failure">
                {data.failure_count}
                {data.failure_count > 0 && <XCircle className="w-4 h-4" />}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] text-[#4B5563] font-semibold uppercase tracking-wider">
            <Filter className="w-3 h-3" /> Filters
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-[10px] h-6" data-testid="clear-filters-btn">
                <X className="w-3 h-3 mr-1" /> Clear all
              </Button>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <FilterField label="Automation">
              <select
                value={automationId}
                onChange={(e) => { setAutomationId(e.target.value); setPage(1); }}
                className="w-full h-9 border border-[#E5E7EB] rounded-md px-3 text-[12px] bg-white"
                data-testid="filter-automation"
              >
                {AUTOMATION_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </FilterField>
            <FilterField label="Status">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="w-full h-9 border border-[#E5E7EB] rounded-md px-3 text-[12px] bg-white"
                data-testid="filter-status"
              >
                <option value="">All statuses</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </FilterField>
            <FilterField label="Triggered by">
              <select
                value={triggeredBy}
                onChange={(e) => { setTriggeredBy(e.target.value); setPage(1); }}
                className="w-full h-9 border border-[#E5E7EB] rounded-md px-3 text-[12px] bg-white"
                data-testid="filter-triggered-by"
              >
                <option value="">All sources</option>
                {(data?.trigger_sources || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FilterField>
            <FilterField label="Since (UTC)">
              <Input type="datetime-local" value={since ? since.substring(0, 16) : ''}
                onChange={(e) => { setSince(e.target.value ? `${e.target.value}:00Z` : ''); setPage(1); }}
                className="h-9 text-[12px]" data-testid="filter-since" />
            </FilterField>
            <FilterField label="Until (UTC)">
              <Input type="datetime-local" value={until ? until.substring(0, 16) : ''}
                onChange={(e) => { setUntil(e.target.value ? `${e.target.value}:59Z` : ''); setPage(1); }}
                className="h-9 text-[12px]" data-testid="filter-until" />
            </FilterField>
            <FilterField label="Search (error text)">
              <div className="relative">
                <Search className="w-3 h-3 text-[#9CA3AF] absolute left-2.5 top-3" />
                <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="e.g. timeout, Resend, permission"
                  className="h-9 text-[12px] pl-7" data-testid="filter-search" />
              </div>
            </FilterField>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-[#9CA3AF]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : !data || data.runs.length === 0 ? (
            <div className="py-16 text-center text-[#9CA3AF] text-[12px]" data-testid="no-runs-empty-state">
              No automation runs match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="runs-table">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr className="text-left">
                    <Th>Started (UTC)</Th>
                    <Th>Automation</Th>
                    <Th>Status</Th>
                    <Th>Duration</Th>
                    <Th>Triggered by</Th>
                    <Th>Summary</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map(run => (
                    <tr
                      key={run.run_id}
                      onClick={() => setSelectedRunId(run.run_id)}
                      className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                      data-testid={`run-row-${run.run_id}`}
                    >
                      <Td mono>{run.created_at ? format(new Date(run.created_at), 'MMM d HH:mm:ss') : '—'}</Td>
                      <Td>{run.automation_name}</Td>
                      <Td>
                        <Badge className={`text-[9px] ${run.status === 'success' ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'}`}>
                          {run.status === 'success' ? <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> : <XCircle className="w-2.5 h-2.5 mr-1" />}
                          {run.status}
                        </Badge>
                      </Td>
                      <Td mono>{run.duration_ms || 0} ms</Td>
                      <Td>{run.triggered_by || '—'}</Td>
                      <Td>
                        <span className="text-[11px] text-[#4B5563] line-clamp-1 max-w-md inline-block">
                          {run.error
                            ? <span className="text-[#FF3B30]">{run.error.substring(0, 120)}</span>
                            : summarizeOutcome(run.outcome)}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > pageSize && (
        <div className="flex items-center justify-between text-[12px]" data-testid="runs-pagination">
          <span className="text-[#6B7280]">
            Page {page} of {totalPages} · showing {data.runs.length} of {data.total}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="page-prev-btn">
              <ChevronLeft className="w-3 h-3" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="page-next-btn">
              Next <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <AutomationRunDetailsModal
        runId={selectedRunId}
        open={!!selectedRunId}
        onOpenChange={(v) => { if (!v) setSelectedRunId(null); }}
      />
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">{label}</p>
      {children}
    </div>
  );
}
function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">{children}</th>;
}
function Td({ children, mono }) {
  return <td className={`px-3 py-2 text-[#0A0A0A] align-middle ${mono ? 'font-mono text-[11px]' : ''}`}>{children}</td>;
}

function summarizeOutcome(outcome) {
  if (!outcome || typeof outcome !== 'object' || Object.keys(outcome).length === 0) return '—';
  // Pick a couple of salient fields if present, else compact JSON
  const preferred = ['compliance_rate', 'reminded', 'dispatched', 'recipients', 'total_scanned', 'passed', 'total'];
  const parts = [];
  for (const k of preferred) {
    if (outcome[k] !== undefined) parts.push(`${k}: ${outcome[k]}`);
  }
  if (parts.length) return parts.join(' · ');
  return JSON.stringify(outcome).substring(0, 120);
}
